import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../auth/AuthContext';
import { adminDownloadsGet, type AdminGetDownloads_Data } from './Downloads';
import { LoadingAnimatedInline } from '../components/Loading';

type EditableUserSoftwarePermission = {
  software: string;
  activated: boolean;
  startsAt: string;
  endsAt: string;
};

type EditableUser = {
  id: string;
  email: string;
  username: string;
  clientName: string;
  activated: boolean;
  legacyId: number;
  groups: string[];
  softwarePermissions?: EditableUserSoftwarePermission[];
};

type SearchResult = {
  id: string;
  email: string;
};

const GROUP_OPTIONS = [
  'groupAdministrator',
  'groupAuthor',
  'groupEditor',
  'groupManager',
  'groupPublisher',
  'groupRegistered',
  'groupSuperUser'
];

const EditUsers: React.FC<{
  initialUserData?: { id: string; email: string } | null;
  onUserUpdated?: () => void;
}> = ({ initialUserData, onUserUpdated }) => {
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedUser, setSelectedUser] = useState<EditableUser | null>(null);
  const [formData, setFormData] = useState<EditableUser | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [downloads, setDownloads] = useState<AdminGetDownloads_Data[]>([]);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Fetch available software on mount
  useEffect(() => {
    const fetchDownloads = async () => {
      try {
        const data = await adminDownloadsGet();
        setDownloads(data);
      } catch (e) {
        console.error('Error fetching downloads:', e);
      }
    };
    fetchDownloads();
  }, []);

  useEffect(() => {
    if (initialUserData) {
      loadUserData(initialUserData.id);
    }
  }, [initialUserData]);

  const loadUserData = async (userId: string) => {
    setLoading(true);
    setError(null);
    try {
      if (!user) throw new Error('Not authenticated');
      const token = await user.getIdToken();

      const res = await fetch('/api/admin/users/get', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ userId })
      });

      if (!res.ok) throw new Error('Failed to load user');
      const data = await res.json();
      const softwarePerms: EditableUserSoftwarePermission[] = [];
      if (data.softwarePerms) {
        for (const [software, perm] of Object.entries(data.softwarePerms)) {
          softwarePerms.push({
            software,
            // @ts-ignore
            activated: perm.activated,
            // @ts-ignore
            startsAt: new Date(perm.startsAt._seconds * 1000).toISOString(),
            // @ts-ignore
            endsAt: new Date(perm.endsAt._seconds * 1000).toISOString()
          });
        }
      }
      const userData: EditableUser = {
        id: data.id,
        email: data.email,
        username: data.username || '',
        clientName: data.clientName || '',
        activated: data.activated === true,
        legacyId: data.legacyId || 0,
        groups: Array.isArray(data.groups) ? data.groups : [],
        softwarePermissions: softwarePerms || []
      };
      setSelectedUser(userData);
      setFormData(userData);
    } catch (e) {
      console.error('Error loading user:', e);
      setError(e instanceof Error ? e.message : 'Failed to load user');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const searchUsers = async () => {
      if (!searchQuery.trim()) {
        setSearchResults([]);
        setShowDropdown(false);
        return;
      }
      try {
        if (!user) throw new Error('Not authenticated');
        const token = await user.getIdToken();

        const res = await fetch('/api/admin/users/search', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({ query: searchQuery })
        });

        if (!res.ok) throw new Error('Search failed');
        const data = await res.json();
        setSearchResults((data.users || []).slice(0, 5));
        setShowDropdown(true);
      } catch (e) {
        console.error('Error searching users:', e);
        setError('Failed to search users');
      }
    };

    const debounceTimer = setTimeout(searchUsers, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchQuery, user]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        searchInputRef.current &&
        !searchInputRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelectUser = (result: SearchResult) => {
    setSearchQuery('');
    setShowDropdown(false);
    loadUserData(result.id);
  };

  const handleFormChange = (field: keyof EditableUser, value: any) => {
    if (formData) {
      setFormData({ ...formData, [field]: value });
    }
  };

  const handleSoftwarePermChange = (
    field: keyof EditableUserSoftwarePermission,
    value: any,
    index: number
  ) => {
    if (formData && formData.softwarePermissions) {
      const updatedPerms = [...formData.softwarePermissions];
      updatedPerms[index] = { ...updatedPerms[index], [field]: value };
      setFormData({ ...formData, softwarePermissions: updatedPerms });
    }
  };

  const handleAddSoftwarePermission = (softwareKey: string) => {
    if (
      formData &&
      !formData.softwarePermissions?.some((p) => p.software === softwareKey)
    ) {
      setFormData({
        ...formData,
        softwarePermissions: [
          ...(formData.softwarePermissions || []),
          {
            software: softwareKey,
            activated: true,
            startsAt: new Date().toISOString(),
            endsAt: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString()
          }
        ]
      });
    }
  };

  const handleRemoveSoftwarePermission = (softwareKey: string) => {
    if (formData) {
      setFormData({
        ...formData,
        softwarePermissions: (formData.softwarePermissions || []).filter(
          (p) => p.software !== softwareKey
        )
      });
    }
  };

  const handleAddGroup = (group: string) => {
    if (formData && !formData.groups.includes(group)) {
      setFormData({ ...formData, groups: [...formData.groups, group] });
    }
  };

  const handleRemoveGroup = (group: string) => {
    if (formData) {
      setFormData({
        ...formData,
        groups: formData.groups.filter((g) => g !== group)
      });
    }
  };

  const handleSubmit = async () => {
    if (!formData) return;
    setIsSubmitting(true);
    setError(null);
    try {
      if (!user) throw new Error('Not authenticated');
      const token = await user.getIdToken();

      const res = await fetch('/api/admin/users/update', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (!res.ok) throw new Error('Update failed');
      alert('User updated successfully');
      setSelectedUser(null);
      setFormData(null);
      setSearchQuery('');
      onUserUpdated?.();
    } catch (e) {
      console.error('Error updating user:', e);
      setError(e instanceof Error ? e.message : 'Failed to update user');
    } finally {
      setIsSubmitting(false);
    }
  };

  if(loading) return (
    <LoadingAnimatedInline />
  )

  return (
    <div className="p-6 h-full flex flex-col">
      <h2 className="text-2xl font-bold mb-6">Edit Users</h2>

      {/* Search Bar with Dropdown */}
      <div className="mb-6 relative">
        <div className="flex-1 relative" ref={dropdownRef}>
          <input
            ref={searchInputRef}
            type="search"
            placeholder="Search by email"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={() => searchQuery && setShowDropdown(true)}
            className="w-full border border-light-border p-2 rounded-md"
          />
          {showDropdown && searchResults.length > 0 && (
            <div className="absolute top-full left-0 right-0 bg-white border border-light-border border-t-0 rounded-b-md shadow-lg z-50">
              {searchResults.map((result) => (
                <button
                  key={result.id}
                  onClick={() => handleSelectUser(result)}
                  className="w-full text-left px-4 py-2 hover:bg-light-main-hover hover:text-light-text-alt transition-colors text-sm border-b border-light-border last:border-b-0"
                >
                  {result.email}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md">
          {error}
        </div>
      )}

      {/* Form */}
      {formData && (
        <div className="flex-1 overflow-y-auto border border-light-border rounded-md p-6">
          <div className="max-w-2xl space-y-6">
            {/* Email (read-only) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email
              </label>
              <input
                type="email"
                value={formData.email}
                disabled
                className="w-full border border-light-border p-2 rounded-md bg-gray-50 text-gray-600 cursor-not-allowed"
              />
            </div>

            {/* Username */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Username
              </label>
              <input
                type="text"
                value={formData.username}
                onChange={(e) => handleFormChange('username', e.target.value)}
                className="w-full border border-light-border p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-light-main"
              />
            </div>

            {/* Client Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Client Name
              </label>
              <input
                type="text"
                value={formData.clientName}
                onChange={(e) => handleFormChange('clientName', e.target.value)}
                className="w-full border border-light-border p-2 rounded-md focus:outline-none focus:ring-2 focus:ring-light-main"
              />
            </div>

            {/* Activated */}
            <div>
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <input
                  type="checkbox"
                  checked={formData.activated}
                  onChange={(e) =>
                    handleFormChange('activated', e.target.checked)
                  }
                  className="w-4 h-4 rounded border-light-border"
                />
                Account Activated
              </label>
            </div>

            {/* Groups */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Groups
              </label>
              <div className="space-y-3">
                <div className="bg-gray-50 rounded-md p-4 min-h-12">
                  <div className="text-xs text-gray-600 mb-2 font-semibold">
                    Selected:
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {formData.groups.length > 0 ? (
                      formData.groups.map((group) => (
                        <div
                          key={group}
                          className="bg-light-main text-light-text-alt px-3 py-1 rounded text-sm flex items-center gap-2"
                        >
                          {group}
                          <button
                            onClick={() => handleRemoveGroup(group)}
                            className="ml-1 hover:opacity-70 transition-opacity"
                            type="button"
                          >
                            ✕
                          </button>
                        </div>
                      ))
                    ) : (
                      <span className="text-gray-400 text-sm">
                        No groups selected
                      </span>
                    )}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-600 mb-2 font-semibold">
                    Available Groups:
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {GROUP_OPTIONS.map((group) => (
                      <button
                        key={group}
                        onClick={() => handleAddGroup(group)}
                        disabled={formData.groups.includes(group)}
                        className="text-left px-3 py-2 border border-light-border rounded hover:bg-light-main-hover hover:text-light-text-alt transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                        type="button"
                      >
                        + {group}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Software Permissions */}
            <div className="pt-6 border-t border-light-border">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                Software Permissions
              </h3>

              {/* Selected permissions */}
              <div className="text-xs text-gray-600 mb-2 font-semibold">
                Selected Software Permissions
              </div>
              <div className="space-y-3 mb-4">
                {formData.softwarePermissions &&
                formData.softwarePermissions.length > 0 ? (
                  formData.softwarePermissions.map((perm, index) => (
                    <div
                      key={perm.software}
                      className="grid grid-cols-4 bg-gray-50 text-gray-700 text-sm rounded-md p-4"
                    >
                      <div className="flex flex-col gap-1">
                        <span className="font-semibold">{perm.software}</span>
                        <label className="flex items-center gap-2 text-xs text-gray-500">
                          <input
                            type="checkbox"
                            checked={perm.activated}
                            onChange={(e) =>
                              handleSoftwarePermChange(
                                'activated',
                                e.target.checked,
                                index
                              )
                            }
                            className="w-3 h-3"
                          />
                          Activated
                        </label>
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500">
                          Start Date
                        </label>
                        <input
                          type="date"
                          value={perm.startsAt.split('T')[0]}
                          onChange={(e) =>
                            handleSoftwarePermChange(
                              'startsAt',
                              e.target.value,
                              index
                            )
                          }
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500">
                          End Date
                        </label>
                        <input
                          type="date"
                          value={perm.endsAt.split('T')[0]}
                          onChange={(e) =>
                            handleSoftwarePermChange(
                              'endsAt',
                              e.target.value,
                              index
                            )
                          }
                        />
                      </div>
                      <button
                        onClick={() =>
                          handleRemoveSoftwarePermission(perm.software)
                        }
                        className="flex items-center justify-end text-red-500 hover:text-red-700"
                        type="button"
                      >
                        ✕
                      </button>
                    </div>
                  ))
                ) : (
                  <div className="text-gray-400 text-sm">
                    No software permissions selected
                  </div>
                )}
              </div>

              {/* Available software */}
              <div>
                <div className="text-xs text-gray-600 mb-2 font-semibold">
                  Available Software Permissions
                </div>
                <div className="grid grid-cols-2 gap-2">
                  {downloads.length > 0 ? (
                    downloads.map((download) => (
                      <button
                        key={download.key}
                        onClick={() =>
                          handleAddSoftwarePermission(download.key)
                        }
                        disabled={formData.softwarePermissions?.some(
                          (p) => p.software === download.key
                        )}
                        className="text-left px-3 py-2 border border-light-border rounded hover:bg-light-main-hover hover:text-light-text-alt transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm bg-gray-50"
                        type="button"
                      >
                        + {download.displayName}
                      </button>
                    ))
                  ) : (
                    <span className="text-gray-400 text-sm">
                      No software items available
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Submit / Cancel */}
            <div className="flex gap-2 pt-4">
              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className="px-6 py-2 bg-light-main text-white rounded-md hover:bg-light-main-hover transition-colors duration-200 ease-in-out font-medium disabled:opacity-50"
              >
                {isSubmitting ? 'Submitting...' : 'Submit'}
              </button>
              <button
                onClick={() => {
                  setSelectedUser(null);
                  setFormData(null);
                }}
                className="px-6 py-2 border border-light-border rounded-md hover:bg-gray-50 transition-colors duration-200 ease-in-out text-gray-700"
                type="button"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {!selectedUser && (
        <div className="text-center text-gray-500 py-12">
          <p>Search for and select a user to edit</p>
        </div>
      )}
    </div>
  );
};

export default EditUsers;